import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from '../trackActivity';
import { DOMAINS } from '../permissionConfig';

// Mirrors the backend's VISIT_TIMEOUT_MINUTES (activityTrackingSchema.js) —
// not imported, since there's no shared build step between frontend and
// backend here. Only used client-side to decide whether a tab-becomes-
// visible event is worth firing a tracked event for at all; the actual
// "is this a new visit" decision is made authoritatively on the backend by
// getOrCreateVisit comparing against the DB's own last_active_at.
const VISIT_TIMEOUT_MINUTES = 30;

// path -> human-readable dashboard name, built from the single source of
// truth (permissionConfig.js's DOMAINS) so this never drifts out of sync
// with the actual page list. Falls back to the raw path if a route isn't
// listed there (e.g. /admin, /landing, /pod-upload), so nothing tracked is
// ever silently unlabelled.
const PAGE_NAMES = {};
DOMAINS.forEach(d => d.functions.forEach(f => { PAGE_NAMES[f.path] = f.label; }));
PAGE_NAMES['/admin'] = 'Admin Console';
PAGE_NAMES['/landing'] = 'Landing';
PAGE_NAMES['/warehouse'] = 'Warehouse';
PAGE_NAMES['/pod-upload'] = 'POD By Hand Upload';

function pageNameFor(pathname) {
    return PAGE_NAMES[pathname] || pathname;
}

// Mounted once near the top of the route tree (inside <BrowserRouter>,
// sibling to <Routes>) in App.js. Renders nothing — pure side-effect
// component for activity tracking.
//
// Deliberately NOT a heartbeat: there is no interval/timer anywhere in
// here. Both effects below are purely event-driven (a route change, or the
// browser's own visibilitychange event), so a tab merely sitting open never
// ticks anything or advances last_active_at on its own.
export default function ActivityTracker({ user }) {
    const location = useLocation();
    const lastActivityRef = useRef(Date.now());

    // One PAGE_VIEW per route change, including the very first page on load.
    useEffect(() => {
        if (!user?.username) return;
        lastActivityRef.current = Date.now();
        track(user.username, 'PAGE_VIEW', pageNameFor(location.pathname));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, user?.username]);

    // Tab reopened after 30+ minutes idle counts as a fresh visit. Only
    // fires a tracked event if real wall-clock time since the last tracked
    // activity actually exceeds the timeout — briefly glancing at a
    // backgrounded tab doesn't spam PAGE_VIEWs.
    useEffect(() => {
        if (!user?.username) return;
        function onVisible() {
            if (document.visibilityState !== 'visible') return;
            const idleMinutes = (Date.now() - lastActivityRef.current) / 60000;
            if (idleMinutes >= VISIT_TIMEOUT_MINUTES) {
                lastActivityRef.current = Date.now();
                track(user.username, 'PAGE_VIEW', pageNameFor(location.pathname));
            }
        }
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.username]);

    return null;
}
